import type { CreateProductPayload } from "@/modules/catalog/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  catalogModifiersApi,
  catalogProductsApi,
  type CreateVariantPayload,
  type ReorderModifierLinksPayload,
} from "./client";

export const catalogProductsKeys = {
  all: ["catalog", "products"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["catalog", "products", "list", q] as const,
  detail: (id: string) => ["catalog", "products", "detail", id] as const,
  full: (id: string) => ["catalog", "products", "full", id] as const,
};

export const catalogModifiersKeys = {
  all: ["catalog", "modifiers"] as const,
  detail: (id: string) => ["catalog", "modifiers", "detail", id] as const,
};

export function useProductsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: catalogProductsKeys.list(q),
    queryFn: () => catalogProductsApi.list(q),
  });
}

export function useProductQuery(id: string) {
  return useQuery({
    queryKey: catalogProductsKeys.detail(id),
    queryFn: () => catalogProductsApi.get(id),
    enabled: !!id,
  });
}

export function useProductFullQuery(id: string) {
  return useQuery({
    queryKey: catalogProductsKeys.full(id),
    queryFn: () => catalogProductsApi.getFull(id),
    enabled: !!id,
  });
}

export const catalogRetailVariantsKey = ["catalog", "products", "retail-variants"] as const;

export function useRetailVariantsQuery() {
  return useQuery({
    queryKey: catalogRetailVariantsKey,
    queryFn: () => catalogProductsApi.listRetailVariants(),
  });
}

export function useModifiersQuery() {
  return useQuery({
    queryKey: catalogModifiersKeys.all,
    queryFn: () => catalogModifiersApi.list(),
  });
}

export function useModifierQuery(id: string) {
  return useQuery({
    queryKey: catalogModifiersKeys.detail(id),
    queryFn: () => catalogModifiersApi.get(id),
    enabled: !!id,
  });
}

export function useCreateModifier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof catalogModifiersApi.create>[0]) =>
      catalogModifiersApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.all });
    },
  });
}

export function useUpdateModifier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof catalogModifiersApi.update>[1]) =>
      catalogModifiersApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.detail(id) });
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.all });
    },
  });
}

export function useCreateModifierOption(modifierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof catalogModifiersApi.createOption>[1]) =>
      catalogModifiersApi.createOption(modifierId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.detail(modifierId) });
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.all });
    },
  });
}

export function useUpdateModifierOption(modifierId: string, optionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof catalogModifiersApi.updateOption>[2]) =>
      catalogModifiersApi.updateOption(modifierId, optionId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.detail(modifierId) });
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.all });
    },
  });
}

export function useDeleteModifierOption(modifierId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (optionId: string) =>
      catalogModifiersApi.deleteOption(modifierId, optionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.detail(modifierId) });
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.all });
    },
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProductPayload) =>
      catalogProductsApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.all });
    },
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProductPayload) =>
      catalogProductsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.full(id) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.all });
    },
  });
}

export function useCreateVariant(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVariantPayload) =>
      catalogProductsApi.createVariant(productId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.full(productId) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.detail(productId) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.all });
    },
  });
}

export function useUpdateVariant(productId: string, variantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVariantPayload) =>
      catalogProductsApi.updateVariant(productId, variantId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.full(productId) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.detail(productId) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.all });
    },
  });
}

export function useRemoveVariant(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) =>
      catalogProductsApi.removeVariant(productId, variantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.full(productId) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.detail(productId) });
      qc.invalidateQueries({ queryKey: catalogProductsKeys.all });
    },
  });
}

export function useAddModifierLink(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (modifierId: string) =>
      catalogProductsApi.addModifierLink(productId, modifierId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.full(productId) });
    },
  });
}

export function useRemoveModifierLink(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (modifierId: string) =>
      catalogProductsApi.removeModifierLink(productId, modifierId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.full(productId) });
    },
  });
}

export function useReorderModifierLinks(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReorderModifierLinksPayload) =>
      catalogProductsApi.reorderModifierLinks(productId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.full(productId) });
    },
  });
}

export function useDeleteModifier() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => catalogModifiersApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogModifiersKeys.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => catalogProductsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogProductsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
